import React, { createContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';

import { useAuth } from 'contexts/auth';
import courseRequest from 'services/course';
import { US_SCHOOL_LIST, CA_SCHOOL_LIST, MAJOR_LIST, SEMESTER_LIST } from 'utils/constants';
import { useUser2 } from 'contexts/user2';

const defaultForm = {
    courseName: '',
    institutionName: null,
    otherInstitutionName: '',
    fieldOfStudy: null,
    faculty: '',
    semester: null,
    section: '',
    projectName: '',
    start_on: null,
    end_on: null,
};

const PAGES = {
    GENERAL_INFORMATION: 'general-information',
    PROJECT_SETTING: 'project-settings',
};

const LIST_OF_SCHOOLS = [
    ...(window.location.origin === 'https://app.ensightful.ca' ? CA_SCHOOL_LIST : US_SCHOOL_LIST),
    { name: 'Other' },
];

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { auth, changeAccount } = useAuth();
    const { course } = useUser2();

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [currentStep, setCurrentStep] = useState(PAGES?.GENERAL_INFORMATION);
    const [errors, setErrors] = useState({
        'general-information': {
            courseName: '',
            faculty: '',
            institutionName: '',
            otherInstitutionName: '',
            fieldOfStudy: '',
            semester: '',
            section: '',
        },
        'project-settings': {
            projectName: '',
            start_on: '',
            end_on: '',
        },
    });

    const handleChange = async (key, data) => {
        switch (key) {
            case 'courseName': {
                return setForm((prev) => ({ ...prev, courseName: data }));
            }

            case 'institutionName': {
                return setForm((prev) => ({ ...prev, institutionName: data }));
            }

            case 'otherInstitutionName': {
                return setForm((prev) => ({ ...prev, otherInstitutionName: data }));
            }

            case 'fieldOfStudy': {
                return setForm((prev) => ({ ...prev, fieldOfStudy: data }));
            }

            case 'faculty': {
                return setForm((prev) => ({ ...prev, faculty: data }));
            }

            case 'section': {
                return setForm((prev) => ({ ...prev, section: data }));
            }

            case 'semester': {
                return setForm((prev) => ({ ...prev, semester: data }));
            }

            case 'projectName': {
                return setForm((prev) => ({ ...prev, projectName: data }));
            }

            case 'start_on': {
                return setForm((prev) => ({ ...prev, start_on: data }));
            }

            case 'end_on': {
                return setForm((prev) => ({ ...prev, end_on: data }));
            }
            default:
                return;
        }
    };

    const getErrorMessage = (key, data, form) => {
        switch (key) {
            case 'courseName': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Course name cannot be left blank';
            }

            case 'faculty': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Faculty cannot be left blank';
            }

            case 'section': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Section cannot be left blank';
            }

            case 'institutionName': {
                const value = `${data?.value || ''}`.trim();
                return value && value?.length > 0 ? '' : 'You must make a selection';
            }

            case 'otherInstitutionName': {
                if (form?.institutionName?.value !== 'Other') return '';
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Institution name cannot be left blank';
            }

            case 'semester': {
                const value = `${data?.value || ''}`.trim();
                return value && value?.length > 0 ? '' : 'You must make a selection';
            }

            case 'fieldOfStudy': {
                const value = `${data?.value || ''}`.trim();
                return value && value?.length > 0 ? '' : 'You must make a selection';
            }

            case 'projectName': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Project name cannot be left blank';
            }

            case 'start_on': {
                const value = data;
                return value ? '' : 'Please select start date';
            }

            case 'end_on': {
                const value = data;
                return value ? '' : 'Please select end date';
            }
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors[currentStep])
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    const handlePrev = (key) => {
        return setCurrentStep(key);
    };

    const handleNext = (key) => {
        if (!validateAll()) return;
        const data = {
            course_id: auth.course_id,
            course_name: form.courseName,
            section: form.section,
            faculty: form.faculty,
            school:
                form.institutionName?.value === 'Other' && form.otherInstitutionName?.length > 0
                    ? form.otherInstitutionName
                    : form.institutionName?.value,
            semester: form.semester?.value,
            field_of_study: form.fieldOfStudy?.value,
            finished_course_set_up: false,
        };
        courseRequest.updateCourse(auth.course_id, data);
        return setCurrentStep(key);
    };

    const handleClose = () => {
        const data = {
            course_id: auth.course_id,
            course_name: form.courseName,
            section: form.section,
            faculty: form.faculty,
            school:
                form.institutionName?.value === 'Other' && form.otherInstitutionName?.length > 0
                    ? form.otherInstitutionName
                    : form.institutionName?.value,
            semester: form.semester?.value,
            field_of_study: form.fieldOfStudy?.value,
            project_title: form.projectName,
            start_on: form.start_on?.valueOf(),
            end_on: form.end_on?.valueOf(),
            root_url: window.location.origin,
            finished_course_set_up: false,
        };
        courseRequest.updateCourse(auth.course_id, data);
        return changeAccount();
    };

    const submitData = async () => {
        if (!validateAll()) return;
        setLoading(true);
        const data = {
            course_id: auth.course_id,
            course_name: form.courseName,
            section: form.section,
            faculty: form.faculty,
            school:
                form.institutionName?.value === 'Other' && form.otherInstitutionName?.length > 0
                    ? form.otherInstitutionName
                    : form.institutionName?.value,
            semester: form.semester?.value,
            field_of_study: form.fieldOfStudy?.value,
            project_title: form.projectName,
            start_on: form.start_on?.valueOf(),
            end_on: form.end_on?.valueOf(),
            root_url: window.location.origin,
            finished_course_set_up: true,
        };
        await courseRequest.createCourse(auth.course_id, data);
        setLoading(false);
        return history.push('/');
    };

    useEffect(() => {
        if (course) {
            const requestCourseSetUpDraft = async () => {
                setLoading(true);
                const existingCourseData = course;
                if (existingCourseData?.finished_course_set_up) return history.push('/');
                const getCourseDraft = await courseRequest.findCourseDraft(course.id);
                const selectedSchool = LIST_OF_SCHOOLS?.find(
                    ({ name }) => name === (getCourseDraft?.school || existingCourseData?.school)
                );
                const selectedMajor = MAJOR_LIST?.find(
                    ({ name }) => name === (getCourseDraft?.field_of_study || existingCourseData?.field_of_study)
                );
                const selectedSemester = SEMESTER_LIST?.find(
                    ({ title }) => title === (getCourseDraft?.semester || existingCourseData?.semester)
                );
                const startOnDate = getCourseDraft?.start_on || existingCourseData?.start_on;
                const endOnDate = getCourseDraft?.end_on || existingCourseData?.end_on;
                const incomingCourseData = {
                    courseName: getCourseDraft?.course_name || existingCourseData?.course_name || '',
                    faculty: getCourseDraft?.faculty || existingCourseData?.faculty || '',
                    section: getCourseDraft?.section || existingCourseData?.section || '',
                    projectName: getCourseDraft?.project_title || existingCourseData?.project_title || '',
                    start_on: startOnDate ? moment(startOnDate) : null,
                    end_on: endOnDate ? moment(endOnDate) : null,

                    institutionName: selectedSchool ? { key: selectedSchool.name, value: selectedSchool.name } : null,
                    fieldOfStudy: selectedMajor ? { key: selectedMajor.name, value: selectedMajor.name } : null,
                    semester: selectedSemester ? { key: selectedSemester.value, value: selectedSemester.title } : null,

                    is_lti: process.env.REACT_APP_AUTH === 'LTI',
                };
                setForm((prev) => ({ ...prev, ...incomingCourseData }));
                setLoading(false);
            };
            requestCourseSetUpDraft();
        }
    }, [course]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                errors,
                currentStep,

                handleChange,
                handlePrev,
                handleNext,
                handleClose,
                submitData,
                setErrors,
                setForm,
                PAGES,
                LIST_OF_SCHOOLS,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
